import React, { memo, useEffect, useMemo, useState } from 'react';
import { createApiUrl } from '../../../config/api';
import PlotlyViewer from './PlotlyViewer';

interface PlotItem {
  file_name: string;
  file_type: string;
  plot_json?: string; // For Plotly plots
}

interface PlotGridProps {
  plots: PlotItem[];
  sessionId?: string;
  userID?: string;
}

const PlotGrid: React.FC<PlotGridProps> = ({ plots, sessionId, userID }) => {
  const [plotImages, setPlotImages] = useState<Array<{ file_name: string; url: string; error?: boolean }>>([]);
  const [loading, setLoading] = useState(true);

  // Memoize the plot file names to detect actual changes
  const plotSignature = useMemo(() => {
    return plots.map(plot => `${plot.file_name}-${plot.plot_json ? 'plotly' : 'image'}`).sort().join(',');
  }, [plots]);

  useEffect(() => {
    const fetchPlotImages = async () => {
      if (plots.length === 0) {
        setPlotImages([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        // For plots with plot_json (Plotly plots), we don't need to fetch anything
        // For image plots, we need to construct the URL
        const imagePromises = plots.map(async (plot) => {
          if (plot.plot_json) {
            // Plotly plot - no URL needed, we have the JSON
            return {
              file_name: plot.file_name,
              url: '',
              error: false
            };
          } else if (sessionId && userID) {
            // Image plot - construct URL
            try {
              const url = createApiUrl(`/file_handling/users/${userID}/sessions/${sessionId}/files/processed/file_types/plot/${encodeURIComponent(plot.file_name)}`);
              
              return {
                file_name: plot.file_name,
                url: url,
                error: false
              };
            } catch (error) {
              console.error(`Failed to fetch plot ${plot.file_name}:`, error);
              return {
                file_name: plot.file_name,
                url: '',
                error: true
              };
            }
          } else {
            // No session/user ID for image plot
            return {
              file_name: plot.file_name,
              url: '',
              error: true
            };
          }
        });

        const results = await Promise.all(imagePromises);
        setPlotImages(results);
      } catch (error) {
        console.error('Error fetching plot images:', error);
        setPlotImages(plots.map(plot => ({
          file_name: plot.file_name,
          url: '',
          error: true
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchPlotImages();
  }, [plotSignature, plots, sessionId, userID]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading plots...</div>
      </div>
    );
  }

  if (plotImages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No plots available</div>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-y-auto h-full scrollbar-hide">
      <div className="grid grid-cols-1 gap-4">
        {plotImages.map((plotImage) => {
          // Check if this is a Plotly plot (has plot_json)
          const plotItem = plots.find(p => p.file_name === plotImage.file_name);
          if (plotItem?.plot_json) {
            return (
              <div key={plotImage.file_name} className="flex flex-col">
                <div className="bg-white rounded-lg border shadow overflow-hidden">
                  <PlotlyViewer 
                    plotJson={plotItem.plot_json}
                  />
                </div>
              </div>
            );
          }
          
          // Otherwise, render as static image
          return (
            <div key={plotImage.file_name} className="flex flex-col">
              <div className="bg-white rounded-lg border shadow overflow-hidden">
                {plotImage.error ? (
                  <div className="w-full h-96 flex items-center justify-center bg-gray-100">
                    <div className="text-center text-gray-500">
                      <div className="text-sm font-medium">Failed to load plot</div>
                      <div className="text-xs mt-1">{plotImage.file_name}</div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={plotImage.url}
                    alt={plotImage.file_name}
                    className="w-full h-auto max-h-96 object-contain"
                    loading="lazy"
                    onError={(e) => {
                      console.error(`Failed to load image: ${plotImage.file_name}`);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      // Show error state by updating the parent div
                      const parentDiv = target.parentElement;
                      if (parentDiv) {
                        parentDiv.innerHTML = `
                          <div class="w-full h-96 flex items-center justify-center bg-gray-100">
                            <div class="text-center text-gray-500">
                              <div class="text-sm font-medium">Failed to load plot</div>
                              <div class="text-xs mt-1">${plotImage.file_name}</div>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(PlotGrid);
